import { useState, useCallback, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';

export default function usePipelines() {
  const { showSuccess, showError } = useToast();
  const posthog = usePostHog();

  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pipeline editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);

  const fetchPipelines = useCallback(async () => {
    try {
      const response = await apiRequest('/crm/pipelines/');
      if (response.ok) {
        const data = await response.json();
        setPipelines(data);
        return data;
      }
    } catch (error) {
      showError('Failed to load pipelines');
    } finally {
      setLoading(false);
    }
    return [];
  }, [showError]);

  const fetchPipeline = useCallback(async (id) => {
    try {
      const response = await apiRequest(`/crm/pipelines/${id}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      showError('Failed to load pipeline');
    }
    return null;
  }, [showError]);

  const createPipeline = async (data) => {
    try {
      const response = await apiRequest('/crm/pipelines/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (response.ok) {
        const pipeline = await response.json();
        showSuccess('Pipeline created');
        posthog?.capture('crm_pipeline_created', { pipeline_id: pipeline.id, pipeline_name: data.name });
        fetchPipelines();
        return pipeline;
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to create pipeline');
      }
    } catch (error) {
      showError('Failed to create pipeline');
    }
    return null;
  };

  const updatePipeline = async (id, data) => {
    try {
      const response = await apiRequest(`/crm/pipelines/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (response.ok) {
        showSuccess('Pipeline updated');
        fetchPipelines();
        return await response.json();
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to update pipeline');
      }
    } catch (error) {
      showError('Failed to update pipeline');
    }
    return null;
  };

  const deletePipeline = async (id) => {
    try {
      const response = await apiRequest(`/crm/pipelines/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        showSuccess('Pipeline deleted');
        fetchPipelines();
        return true;
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to delete pipeline');
      }
    } catch (error) {
      showError('Failed to delete pipeline');
    }
    return false;
  };

  // Stage management
  const createStage = async (pipelineId, data) => {
    try {
      const response = await apiRequest(`/crm/pipelines/${pipelineId}/stages/`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (response.ok) {
        return await response.json();
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to create stage');
      }
    } catch (error) {
      showError('Failed to create stage');
    }
    return null;
  };

  const updateStage = async (pipelineId, stageId, data) => {
    try {
      const response = await apiRequest(`/crm/pipelines/${pipelineId}/stages/${stageId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (response.ok) {
        return await response.json();
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to update stage');
      }
    } catch (error) {
      showError('Failed to update stage');
    }
    return null;
  };

  const deleteStage = async (pipelineId, stageId) => {
    try {
      const response = await apiRequest(`/crm/pipelines/${pipelineId}/stages/${stageId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        return true;
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to delete stage');
      }
    } catch (error) {
      showError('Failed to delete stage');
    }
    return false;
  };

  const reorderStages = async (pipelineId, stages) => {
    try {
      const response = await apiRequest(`/crm/pipelines/${pipelineId}/stages/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ stages }),
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      showError('Failed to reorder stages');
    }
    return null;
  };

  // Deal operations
  const moveDeal = async (dealId, stageId) => {
    try {
      const response = await apiRequest(`/crm/pipelines/deals/${dealId}/move`, {
        method: 'PUT',
        body: JSON.stringify({ stage_id: stageId }),
      });
      if (response.ok) {
        const result = await response.json();
        posthog?.capture('crm_deal_moved', { deal_id: dealId, to_stage_id: stageId });
        return result;
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to move deal');
      }
    } catch (error) {
      showError('Failed to move deal');
    }
    return null;
  };

  const getDealEvents = async (dealId, skip = 0, limit = 50) => {
    try {
      const response = await apiRequest(`/crm/pipelines/deals/${dealId}/events?skip=${skip}&limit=${limit}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      showError('Failed to load deal events');
    }
    return { items: [], total: 0 };
  };

  useEffect(() => {
    fetchPipelines();
  }, [fetchPipelines]);

  return {
    // Data
    pipelines,
    selectedPipeline,
    setSelectedPipeline,
    loading,

    // Pipeline CRUD
    fetchPipelines,
    fetchPipeline,
    createPipeline,
    updatePipeline,
    deletePipeline,

    // Stage management
    createStage,
    updateStage,
    deleteStage,
    reorderStages,

    // Deal operations
    moveDeal,
    getDealEvents,

    // Editor state
    showEditor,
    setShowEditor,
    editingPipeline,
    setEditingPipeline,
  };
}
